import { z } from 'zod';
import { IsoDateTime, PageQuery, Uuid, page, patchOf } from './common.js';
/** Module `safety-check` (docs/modules/safety-check.md, ADR 0026). Owner: task S23. */
export const BlockEntry = z.object({
    id: Uuid,
    fullName: z.string(),
    aliases: z.array(z.string()),
    company: z.string(),
    /** Free text shown to people with safety_check.view, e.g. "Abusive to staff, March 2026". */
    criteria: z.string(),
    /** null = every site. */
    siteIds: z.array(Uuid).nullable(),
    expiresOn: z.string().nullable(),
    active: z.boolean(),
    updatedAt: IsoDateTime,
    updatedByLabel: z.string(),
});
export const BlockEntryCreate = z.object({
    fullName: z.string().trim().min(2).max(120),
    aliases: z.array(z.string().trim().min(2).max(120)).max(10).default([]),
    company: z.string().trim().max(120).default(''),
    criteria: z.string().trim().max(1000).default(''),
    siteIds: z.array(Uuid).max(500).nullable().default(null),
    expiresOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null),
});
export const BlockEntryUpdate = patchOf(BlockEntryCreate).extend({ active: z.boolean().optional() });
export const BlockListQuery = PageQuery.extend({ q: z.string().max(100).optional(), status: z.enum(['active', 'inactive', 'all']).default('active') });
export const BlockList = page(BlockEntry);
/** POST /safety-check/test: "would this name be caught?" (no visit made). */
export const BlockTest = z.object({ fullName: z.string().trim().min(1).max(120), siteId: Uuid.nullable().default(null) });
export const BlockTestResult = z.object({ matched: z.boolean(), entryIds: z.array(Uuid) });
/** Recent matches (refused or allowed with an alert), GET /safety-check/matches. */
export const BlockMatch = z.object({
    id: Uuid,
    entryId: Uuid,
    siteId: Uuid,
    groupId: Uuid,
    visitId: Uuid.nullable(),
    name: z.string(),
    outcome: z.enum(['refused', 'allowed_with_alert', 'offline_warning', 'overridden']),
    source: z.string(),
    occurredAt: IsoDateTime,
});
export const BlockMatchList = page(BlockMatch);
/** Kiosk config section `safety-check` (ADR 0026): no readable names. */
export const KioskSafetySection = z.object({
    /** HMAC(siteKey, nameKey(name)) hex per active name and alias valid at this site. */
    keys: z.array(z.string()),
    /** groupId -> mode, only groups whose mode isn't off. */
    modes: z.record(z.string(), z.enum(['refuse', 'allow_with_alert'])),
});
export const SafetyCheckGroupSettings = z.object({
    mode: z.enum(['off', 'refuse', 'allow_with_alert']).default('refuse'),
});
export const SafetyCheckSiteSettings = z.object({
    /** Portal users with today.sign_in_out may override a refusal (audited). */
    allowOverride: z.boolean().default(false),
});
export const SafetyCheckTenantSettings = z.object({
    /** Days to keep match records; 0 = keep. */
    matchRetentionDays: z.number().int().min(0).default(365),
});
//# sourceMappingURL=safety-check.js.map